type SnapFrameOptions = {
    timePrecision: number;
    frameRate: number;
};

type GenericSnapFrameOptions = {
    roundingMethod: (n: number) => number;
    timePrecision: number;
    frameRate: number;
};

/**
 * Round a number to a specified precision (number of significant digits after the decimal)
 */
export function roundToPrecision(value: number, precision: number): number {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
}

/**
 * Tries to snap the time down to the previous frame boundary. However, if the given time is
 * already very close to a boundary, the time is snapped to that boundary even if it means
 * slightly increasing the time.
 */
export function snapTimeDownToPreviousFrameBoundary(timeSec: number, options: SnapFrameOptions): number {
    return snapTimeToFrameBoundary(timeSec, { ...options, roundingMethod: Math.floor });
}

/**
 * Tries to snap the time up to the next frame boundary. However, if the given time is already
 * very close to a boundary, the time is snapped to that boundary even if it means slightly
 * decreasing the time.
 */
export function snapTimeUpToNextFrameBoundary(timeSec: number, options: SnapFrameOptions): number {
    return snapTimeToFrameBoundary(timeSec, { ...options, roundingMethod: Math.ceil });
}

/**
 * Handles safe conversions from time to the corresponding frame index. This method tries to
 * account for floating point issues (like 0.9 - 0.3) and properly finds the nearest frame
 * boundary. Example at 30fps:
 * 033.3200ms => frame 0
 * 033.3334ms => frame 0 (precision of 6, truncates before 0.1 microsecond)
 * 033.3340ms => frame 1 (precision of 6, includes 4 in microsecond place)
 * 033.3400ms => frame 1
 */
export function timeToFrameIndex(timeSec: number, options: SnapFrameOptions & { snapFrameToBoundaryIfClose: boolean; }): number {
    const roundedTime = roundToPrecision(timeSec, options.timePrecision);
    const nonIntegerFrame = roundedTime * options.frameRate;
    const frameIndex = options.snapFrameToBoundaryIfClose ?
        snapFrameToBoundaryIfClose(nonIntegerFrame, options.timePrecision) : nonIntegerFrame;
    return Math.floor(frameIndex);
}

/**
 * Snaps to a frame boundary, if the boundary is close by. The argument should be a non-rounded
 * frame index. If a frame boundary is close, the return value will be the exact
 * (rounded) frame, otherwise the input is returned.
 */
function snapFrameToBoundaryIfClose(nonIntegerFrame: number, timePrecision: number): number {
    const roundedFrame = Math.round(nonIntegerFrame);
    const diff = Math.abs(roundedFrame - nonIntegerFrame);
    // Frame vs time is ~2 orders of magnitude different (e.g. factor of 30 fps between)
    const framePrecision = timePrecision - 2;
    const delta = Math.pow(10, -framePrecision);
    if (diff < delta) {
        return roundedFrame;
    }
    return nonIntegerFrame;
}

function snapTimeToFrameBoundary(timeSec: number, options: GenericSnapFrameOptions): number {
    const roundedTime = roundToPrecision(timeSec, options.timePrecision);
    const nonIntegerFrame = roundedTime * options.frameRate;
    const snappedFrame = snapFrameToBoundaryIfClose(nonIntegerFrame, options.timePrecision);
    const time = options.roundingMethod(snappedFrame) / options.frameRate;
    return roundToPrecision(time, options.timePrecision);
}
